import { useCallback, useEffect, useMemo, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Modal,
    Button,
    Alert,
    Form,
    Input,
    message,
    Steps,
    Typography,
    Card,
    Space,
    Row,
    Col,
    Descriptions,
    Avatar,
    Tag,
    List,
    Spin,
} from 'antd';
import {
    IdcardOutlined,
    ReloadOutlined,
    LockOutlined,
    CheckCircleOutlined,
    ExclamationCircleOutlined,
} from '@ant-design/icons';
import type { StepProps } from 'antd/es/steps';
import {
    deriveDataTile,
    deriveDg2Tile,
    deriveEidIndicator,
    eidErrorMessageKey,
    readEidCard,
    type ChipTileTone,
    type EidCardAddress,
    type EidCardData,
} from '../domain/eidReader';
import { probeHardwareService, type HardwareServiceStatus } from '../domain/labelPrinting';
import { parseRomanianAddress } from '../domain/romanianAddress';

const { Paragraph, Text } = Typography;

/**
 * Reads a Romanian eID through the **workstation hardware service**, not through the backend.
 *
 * The backend serves every shop from one container, so a card reader it can reach is a reader
 * plugged into the server — never the one on the counter. The browser therefore talks to the
 * local service on the loopback, exactly as label printing does. One consequence is worth
 * stating: the **PIN and the CAN never leave the workstation**, where the previous design sent
 * both to the central server on every read.
 */
interface RomanianIdCardDataForForm {
    cnp?: string;
    lastName?: string;
    firstName?: string;
    documentNumber?: string;
    issuedBy?: string;
    validFrom?: string;
    validUntil?: string;
    photoBase64?: string;
    county?: string;
    city?: string;
    street?: string;
    streetNumber?: string;
    building?: string;
    entrance?: string;
    floor?: string;
    apartment?: string;
    postalCode?: string;
    fullAddress?: string;
    /**
     * The workstation that read the card, for the audit the save will write.
     *
     * Carried alongside the card's data rather than looked up again by the page: this modal is
     * what talked to the service, so it is what knows which installation answered.
     */
    installationId?: string | null;
    /**
     * What the read reported about the card photo, for the audit the save will write.
     *
     * Passed through exactly as the service published it, never re-derived from the read's overall
     * status - the service is the only thing that knows, and recomputing it here would put a
     * guess in an audit row.
     */
    photo?: { state: string; code: string } | null;
}

interface IdCardReaderModalProps {
    visible: boolean;
    onClose: () => void;
    onDataRead?: (data: RomanianIdCardDataForForm) => void;
}

/** Card PINs are 4-12 digits; the CAN printed on the card is always 6. */
const PIN_MAX_LENGTH = 12;
const PIN_MIN_LENGTH = 4;
const CAN_LENGTH = 6;
const SERVICE_PROBE_POLL_MS = 3000;

/**
 * What the service reports having read.
 *
 * `DG1` is deliberately absent. The civil record comes from the national DATA applet and the
 * portrait from the eMRTD application; the MRZ data group is never fetched on this path, so
 * listing it would show an operator a row that can only ever say "pending".
 */
const CHIP_DATA_GROUPS = ['DATA', 'DG2'] as const;

type ChipDataGroupId = (typeof CHIP_DATA_GROUPS)[number];

// Only the title is fixed per group. The sentence beneath it depends on what the read reported,
// so it comes from the tile derivation rather than from a constant.
const CHIP_TRANSLATIONS: Record<ChipDataGroupId, { titleKey: string }> = {
    DATA: { titleKey: 'idCardReader.dataTitle' },
    DG2: { titleKey: 'idCardReader.dg2Title' },
};

/** The tag colour for each tone. `warning` is orange and, deliberately, never red. */
const TONE_COLOURS: Record<ChipTileTone, string> = {
    success: 'green',
    processing: 'blue',
    warning: 'orange',
    error: 'red',
    neutral: 'default',
};

type ReaderStatus = 'idle' | 'reading' | 'success' | 'error';

/** The page's own fetch, bound once so the domain module stays free of globals. */
const browserFetch = (input: string, init?: RequestInit) => window.fetch(input, init);

export const IdCardReaderModal = ({ visible, onClose, onDataRead }: IdCardReaderModalProps) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [cardData, setCardData] = useState<EidCardData | null>(null);
    const [status, setStatus] = useState<ReaderStatus>('idle');
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [serviceStatus, setServiceStatus] = useState<HardwareServiceStatus | null>(null);

    const pinValue = Form.useWatch('pin', form) as string | undefined;
    const isPinValid = typeof pinValue === 'string' && /^\d{4,12}$/.test(pinValue);
    const readerIndicator = deriveEidIndicator(serviceStatus);

    const checkServiceStatus = useCallback(async () => {
        setServiceStatus(await probeHardwareService(browserFetch));
    }, []);

    useEffect(() => {
        if (!visible) {
            form.resetFields();
            setCardData(null);
            setStatus('idle');
            setErrorMessage(null);
            setServiceStatus(null);
        }
    }, [visible, form]);

    // While the modal is open, ask the local service whether this workstation can read. The
    // probe is two plain HTTP calls to the service and never touches the card, so unlike the
    // backend probe it replaced it cannot steal the card from a read in progress. It is still
    // paused during a read, because a status change mid-read would only confuse the display.
    useEffect(() => {
        if (!visible || status === 'reading') {
            return;
        }
        void checkServiceStatus();
        const interval = setInterval(() => {
            void checkServiceStatus();
        }, SERVICE_PROBE_POLL_MS);
        return () => clearInterval(interval);
    }, [visible, checkServiceStatus, status]);

    const steps: StepProps[] = useMemo(() => {
        // Extract nested ternary to improve readability
        let readStatus: StepProps['status'] = 'wait';
        if (status === 'success') {
            readStatus = 'finish';
        } else if (status === 'reading') {
            readStatus = 'process';
        } else if (status === 'error') {
            readStatus = 'error';
        }

        // Extract nested ternary for pin step status
        let pinStepStatus: StepProps['status'] = 'process';
        if (status === 'error' && !isPinValid) {
            pinStepStatus = 'error';
        } else if (isPinValid) {
            pinStepStatus = 'finish';
        }

        return [
            {
                title: t('idCardReader.stepInsert'),
                status: 'finish',
            },
            {
                title: t('idCardReader.stepPin'),
                status: pinStepStatus,
            },
            {
                title: t('idCardReader.stepRead'),
                status: readStatus,
            },
            {
                title: t('idCardReader.stepComplete'),
                status: status === 'success' ? 'finish' : 'wait',
            },
        ];
    }, [isPinValid, status, t]);

    const currentStep = useMemo(() => {
        if (status === 'success') return 3;
        if (status === 'reading') return 2;
        if (isPinValid) return 1;
        return 0;
    }, [isPinValid, status]);

    /** The address components, split from the one line the chip stores. */
    const address: EidCardAddress = useMemo(
        () => parseRomanianAddress(cardData?.rawAddress),
        [cardData?.rawAddress],
    );

    const isoDataGroups = useMemo(() => {
        // Each tile is derived by its own rule, in the domain module where it is unit-tested. The
        // two rules are deliberately different: DATA inherits the read's overall status, because a
        // failed read is a failure to produce the civil record; DG2 never does, because the
        // portrait may not have been asked for at all and a read that fails for an unrelated
        // reason says nothing about it.
        const tiles = {
            DATA: deriveDataTile(cardData?.civilDataRead === true, status),
            DG2: deriveDg2Tile(cardData?.photo ?? null, status),
        };

        return CHIP_DATA_GROUPS.map(groupId => {
            const tile = tiles[groupId];

            return {
                id: groupId,
                title: t(CHIP_TRANSLATIONS[groupId].titleKey),
                description: t(tile.descriptionKey),
                tone: tile.tone,
                label: t(tile.labelKey),
            };
        });
    }, [cardData?.civilDataRead, cardData?.photo, status, t]);

    const handleReadCard = async () => {
        let pin: string;
        let can: string | undefined;

        try {
            const values = await form.validateFields();
            pin = values.pin;
            can = typeof values.can === 'string' && values.can.length > 0 ? values.can : undefined;
        } catch {
            return;
        }

        setStatus('reading');
        setErrorMessage(null);
        setCardData(null);

        // The CAN is only sent when supplied: it unlocks the face photo, while the PIN unlocks
        // the civil record (including the address). Neither ever reaches the central backend.
        const result = await readEidCard(browserFetch, { pin, can }, serviceStatus ?? undefined);

        if (result.ok) {
            setCardData(result.card);
            setStatus('success');
            message.success(t('idCardReader.readSuccess'));
            return;
        }

        // Every failure is a code with its own sentence, because the useful ones tell the
        // operator what to do next — re-seat the card, check the CAN, stop retrying.
        const readerMessage = t(eidErrorMessageKey(result.code));
        setErrorMessage(readerMessage);
        setStatus('error');
        message.error(readerMessage);
    };

    const handleUseData = () => {
        if (!cardData || !onDataRead) {
            return;
        }

        onDataRead({
            cnp: cardData.cnp,
            lastName: cardData.lastName,
            firstName: cardData.firstName,
            documentNumber: cardData.documentNumber,
            // The service names these as the card does; the form calls them issuer and validity.
            issuedBy: cardData.issuer,
            validFrom: cardData.issuedOn,
            validUntil: cardData.validUntil,
            photoBase64: cardData.photoBase64,
            ...address,
            fullAddress: cardData.rawAddress,
            installationId:
                serviceStatus?.state === 'ready'
                    ? (serviceStatus.health.installationId ?? null)
                    : null,
            photo: cardData.photo ?? null,
        });
        message.success(t('idCardReader.dataUsed'));
        onClose();
    };

    const resetPinField = (event: ChangeEvent<HTMLInputElement>) => {
        const numericValue = event.target.value.replace(/\D/g, '').slice(0, PIN_MAX_LENGTH);
        form.setFieldsValue({ pin: numericValue });
    };

    const resetCanField = (event: ChangeEvent<HTMLInputElement>) => {
        const numericValue = event.target.value.replace(/\D/g, '').slice(0, CAN_LENGTH);
        form.setFieldsValue({ can: numericValue });
    };

    const photoSource = useMemo(() => {
        if (!cardData?.photoBase64) {
            return undefined;
        }

        return `data:image/jpeg;base64,${cardData.photoBase64}`;
    }, [cardData?.photoBase64]);

    return (
        <Modal
            title={
                <Space>
                    <IdcardOutlined />
                    {t('idCardReader.title')}
                </Space>
            }
            open={visible}
            onCancel={onClose}
            width={900}
            footer={[
                <Button key="close" onClick={onClose} icon={<ExclamationCircleOutlined />}>
                    {t('common.close')}
                </Button>,
                <Button
                    key="read"
                    type="default"
                    icon={<ReloadOutlined />}
                    onClick={handleReadCard}
                    loading={status === 'reading'}
                    disabled={!readerIndicator.canRead}
                >
                    {t('idCardReader.readCard')}
                </Button>,
                <Button
                    key="use"
                    type="primary"
                    onClick={handleUseData}
                    disabled={!cardData}
                    icon={<CheckCircleOutlined />}
                >
                    {t('idCardReader.useData')}
                </Button>,
            ]}
        >
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Alert
                    message={t('idCardReader.instructions')}
                    description={t('idCardReader.instructionsDetail')}
                    type="info"
                    showIcon
                />

                <Alert
                    data-testid="reader-status"
                    type={readerIndicator.level}
                    showIcon
                    message={t('idCardReader.statusHeading')}
                    description={t(readerIndicator.messageKey)}
                    action={
                        <Button
                            size="small"
                            icon={<ReloadOutlined />}
                            onClick={() => void checkServiceStatus()}
                        >
                            {t('idCardReader.statusRefresh')}
                        </Button>
                    }
                />

                <Steps current={currentStep} items={steps} responsive />

                <Form layout="vertical" form={form} initialValues={{ pin: '', can: '' }}>
                    <Form.Item
                        name="pin"
                        label={
                            <Space>
                                <LockOutlined />
                                {t('idCardReader.pinLabel')}
                            </Space>
                        }
                        tooltip={t('idCardReader.pinHelper')}
                        rules={[
                            { required: true, message: t('idCardReader.pinValidation') },
                            {
                                pattern: /^\d{4,12}$/,
                                message: t('idCardReader.pinValidation'),
                            },
                        ]}
                    >
                        <Input.Password
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            placeholder={t('idCardReader.pinPlaceholder')}
                            maxLength={PIN_MAX_LENGTH}
                            minLength={PIN_MIN_LENGTH}
                            onChange={resetPinField}
                        />
                    </Form.Item>
                    <Form.Item
                        name="can"
                        label={
                            <Space>
                                <IdcardOutlined />
                                {t('idCardReader.canLabel')}
                            </Space>
                        }
                        tooltip={t('idCardReader.canHelper')}
                        extra={t('idCardReader.canOptional')}
                        rules={[
                            {
                                pattern: /^\d{6}$/,
                                message: t('idCardReader.canValidation'),
                            },
                        ]}
                    >
                        <Input
                            inputMode="numeric"
                            autoComplete="off"
                            placeholder={t('idCardReader.canPlaceholder')}
                            maxLength={CAN_LENGTH}
                            onChange={resetCanField}
                        />
                    </Form.Item>
                </Form>

                <Card title={t('idCardReader.chipDataHeading')} size="small">
                    <List
                        itemLayout="horizontal"
                        dataSource={isoDataGroups}
                        renderItem={item => (
                            <List.Item>
                                <List.Item.Meta
                                    title={
                                        <Space>
                                            <Text strong>{item.id}</Text>
                                            <Text>{item.title}</Text>
                                            <Tag
                                                color={TONE_COLOURS[item.tone]}
                                                data-testid={`chip-group-${item.id}`}
                                                data-tone={item.tone}
                                            >
                                                {item.label}
                                            </Tag>
                                        </Space>
                                    }
                                    description={
                                        <Paragraph style={{ marginBottom: 0 }}>
                                            {item.description}
                                        </Paragraph>
                                    }
                                />
                            </List.Item>
                        )}
                    />
                </Card>

                {errorMessage && (
                    <Alert
                        message={t('idCardReader.errorHeading')}
                        description={errorMessage}
                        type="error"
                        showIcon
                    />
                )}

                <Spin spinning={status === 'reading'}>
                    {cardData ? (
                        <Space direction="vertical" size="large" style={{ width: '100%' }}>
                            <Row gutter={[16, 16]}>
                                <Col xs={24} md={8}>
                                    <Card>
                                        <Space
                                            direction="vertical"
                                            align="center"
                                            style={{ width: '100%' }}
                                        >
                                            <Avatar
                                                shape="square"
                                                size={140}
                                                src={photoSource}
                                                icon={!photoSource && <IdcardOutlined />}
                                            />
                                            <Text strong style={{ textAlign: 'center' }}>
                                                {[cardData.lastName, cardData.firstName]
                                                    .filter(Boolean)
                                                    .join(' ')}
                                            </Text>
                                            {cardData.pinVerified && (
                                                <Tag icon={<CheckCircleOutlined />} color="green">
                                                    {t('idCardReader.pinVerified')}
                                                </Tag>
                                            )}
                                        </Space>
                                    </Card>
                                </Col>
                                <Col xs={24} md={16}>
                                    <Card title={t('idCardReader.personalHeading')} size="small">
                                        <Descriptions column={2} bordered size="small">
                                            <Descriptions.Item
                                                span={2}
                                                label={t('idCardReader.cnp')}
                                            >
                                                {cardData.cnp}
                                            </Descriptions.Item>
                                            <Descriptions.Item label={t('idCardReader.lastName')}>
                                                {cardData.lastName}
                                            </Descriptions.Item>
                                            <Descriptions.Item label={t('idCardReader.firstName')}>
                                                {cardData.firstName}
                                            </Descriptions.Item>
                                            <Descriptions.Item
                                                label={t('idCardReader.dateOfBirth')}
                                            >
                                                {cardData.dateOfBirth}
                                            </Descriptions.Item>
                                            <Descriptions.Item
                                                label={t('idCardReader.placeOfBirth')}
                                            >
                                                {cardData.placeOfBirth}
                                            </Descriptions.Item>
                                            <Descriptions.Item label={t('idCardReader.sex')}>
                                                {(() => {
                                                    // Extract nested ternary for sex display
                                                    if (cardData.sex === 'M') {
                                                        return t('idCardReader.male');
                                                    } else if (cardData.sex === 'F') {
                                                        return t('idCardReader.female');
                                                    }
                                                    return cardData.sex;
                                                })()}
                                            </Descriptions.Item>
                                            <Descriptions.Item
                                                label={t('idCardReader.nationality')}
                                            >
                                                {cardData.nationality}
                                            </Descriptions.Item>
                                        </Descriptions>
                                    </Card>
                                </Col>
                            </Row>

                            <Row gutter={[16, 16]}>
                                <Col xs={24} md={12}>
                                    <Card title={t('idCardReader.documentHeading')} size="small">
                                        <Descriptions bordered column={1} size="small">
                                            <Descriptions.Item
                                                label={t('idCardReader.documentNumber')}
                                            >
                                                {cardData.documentNumber}
                                            </Descriptions.Item>
                                            <Descriptions.Item label={t('idCardReader.validFrom')}>
                                                {cardData.issuedOn}
                                            </Descriptions.Item>
                                            <Descriptions.Item label={t('idCardReader.validUntil')}>
                                                {cardData.validUntil}
                                            </Descriptions.Item>
                                            <Descriptions.Item label={t('idCardReader.issuedBy')}>
                                                {cardData.issuer}
                                            </Descriptions.Item>
                                        </Descriptions>
                                    </Card>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Card title={t('idCardReader.addressHeading')} size="small">
                                        <Descriptions bordered column={1} size="small">
                                            <Descriptions.Item
                                                label={t('idCardReader.fullAddress')}
                                            >
                                                {cardData.rawAddress}
                                            </Descriptions.Item>
                                            <Descriptions.Item label={t('idCardReader.city')}>
                                                {address.city}
                                            </Descriptions.Item>
                                            <Descriptions.Item label={t('idCardReader.county')}>
                                                {address.county}
                                            </Descriptions.Item>
                                            <Descriptions.Item label={t('idCardReader.street')}>
                                                {[address.street, address.streetNumber]
                                                    .filter(Boolean)
                                                    .join(', ')}
                                            </Descriptions.Item>
                                            <Descriptions.Item label={t('idCardReader.building')}>
                                                {[address.building, address.entrance]
                                                    .filter(Boolean)
                                                    .join(', ')}
                                            </Descriptions.Item>
                                            <Descriptions.Item label={t('idCardReader.floor')}>
                                                {[address.floor, address.apartment]
                                                    .filter(Boolean)
                                                    .join(', ')}
                                            </Descriptions.Item>
                                            {address.postalCode && (
                                                <Descriptions.Item
                                                    label={t('idCardReader.postalCode')}
                                                >
                                                    {address.postalCode}
                                                </Descriptions.Item>
                                            )}
                                        </Descriptions>
                                    </Card>
                                </Col>
                            </Row>
                        </Space>
                    ) : (
                        <Alert
                            type="warning"
                            showIcon
                            message={t('idCardReader.awaitingDataTitle')}
                            description={t('idCardReader.awaitingDataDescription')}
                        />
                    )}
                </Spin>
            </Space>
        </Modal>
    );
};
